import { currentStreamingText, upsertTextSegment, type TextStreamSegment } from '@/frontend/features/AgentChats/events/textStreaming'
import type { AgentEvent } from '@/frontend/features/AgentChats/events/agentEvents'

export interface RevisionEdit { before: string; after: string }
export interface RevisionPreviewData {
  key: string
  organization_id: string
  session_id: string
  turn_id: string
  field: 'procedure_markdown' | 'skill_markdown'
  base_version_number: number
  state: 'streaming' | 'pending' | 'applied' | 'failed'
  live_available: boolean
  edits: RevisionEdit[]
  copy: Record<string, string>
}

// Preview only: extract fully received JSON objects; never repair incomplete
// JSON or apply edits to canonical Markdown. The server owns that validation.
export function completedRevisionEdits(text: string, field: string): RevisionEdit[] {
  if (text.length > 128_000) return []
  const source = text.trim().replace(/^```(?:json)?\s*/, '')
  if (!source.startsWith('{')) return []
  let cursor = 1
  const skip = () => { while (/\s/.test(source[cursor] || '') && cursor < source.length) cursor++ }
  try {
    while (cursor < source.length) {
      skip()
      const keyEnd = valueEnd(source, cursor)
      if (keyEnd === null) return []
      const key = JSON.parse(source.slice(cursor, keyEnd))
      cursor = keyEnd
      skip()
      if (source[cursor++] !== ':') return []
      skip()
      if (key === `${field}_edits`) {
        if (source[cursor++] !== '[') return []
        const edits: RevisionEdit[] = []
        while (cursor < source.length && edits.length < 40) {
          skip()
          if (source[cursor] === ']') return edits
          const end = valueEnd(source, cursor)
          if (end === null) return edits
          const edit = JSON.parse(source.slice(cursor, end))
          if (!edit || typeof edit.before !== 'string' || typeof edit.after !== 'string' || !edit.before.trim()) return []
          edits.push({ before: edit.before, after: edit.after })
          cursor = end
          skip()
          if (source[cursor] === ']') return edits
          if (cursor === source.length) return edits
          if (source[cursor++] !== ',') return []
        }
        return edits
      }
      const end = valueEnd(source, cursor)
      if (end === null) return []
      JSON.parse(source.slice(cursor, end))
      cursor = end
      skip()
      if (source[cursor++] !== ',') return []
    }
  } catch { return [] }
  return []
}

function valueEnd(text: string, start: number): number | null {
  let depth = 0, quoted = false, escaped = false
  for (let index = start; index < text.length; index++) {
    const char = text[index]
    if (quoted) {
      if (escaped) escaped = false
      else if (char === '\\') escaped = true
      else if (char === '"') { quoted = false; if (depth === 0) return index + 1 }
    } else if (char === '"') quoted = true
    else if (char === '{' || char === '[') depth++
    else if (char === '}' || char === ']') { if (--depth === 0) return index + 1; if (depth < 0) return index }
    else if (depth === 0 && (char === ',' || /\s/.test(char))) return index
  }
  return null
}

// Keep transport order, item boundaries and replacement semantics. Duplicate
// frames, previous turns and older retry attempts must not duplicate the diff.
export class RevisionStream {
  private seen = new Set<string>()
  private segments: TextStreamSegment[] = []
  private attempt = -1
  private bytes = 0
  private terminal = false
  push(event: AgentEvent, preview: RevisionPreviewData): RevisionEdit[] | null {
    if (event.turn_id !== preview.turn_id) return null
    const attempt = event.attempt || 0
    if (attempt < this.attempt) return null
    if (attempt > this.attempt) { this.seen.clear(); this.segments = []; this.bytes = 0; this.attempt = attempt; this.terminal = false }
    if (['RUN_ERROR', 'RUN_CANCELLED'].includes(event.event_type)) { this.terminal = true; return [] }
    if (this.terminal || event.event_type !== 'TEXT_MESSAGE_CONTENT') return null
    const key = event.id || `${event.seq}:${event.event_type}`
    if ((event.seq === null && !event.id) || this.seen.has(key)) return null
    this.seen.add(key)
    this.bytes += event.payload.delta?.length || 0
    if (this.bytes > 128_000 || this.seen.size > 4_000) { this.terminal = true; this.segments = []; this.seen.clear(); return [] }
    this.segments = upsertTextSegment(this.segments, event)
    const ordered = [...this.segments].sort((a, b) => (a.firstSeq || 0) - (b.firstSeq || 0))
    return completedRevisionEdits(currentStreamingText(ordered), preview.field)
  }
}
